import type { SearchResponse } from "@/redistribution/models/SearchResponse.ts";
import type {
  DemoShippingPoRequest,
  OptimizedShippingPoRequest,
  OptimizedShippingPoResponse,
} from "../features/bulkOrderCreation/optimizedShippingPoGeneration.ts";
import type { AlgorithmType } from "../redistribution/models/AlgorithmType.js";
import type { DemandSourceType } from "../redistribution/models/DemandSourceType.ts";
import type {
  BulkOrderAllocationRow,
  IntermediateOrderSearchDiagnostics,
  IntermediateOrderSearchResult,
  IntermediateOrderSearchSource,
} from "../redistribution/models/IntermediateOrderSearchResult.ts";
import type { IntermediateOrderSkuRow } from "../redistribution/models/IntermediateOrderSkuRow.ts";
import type { RedistributionRequestPurchaseOrder } from "../redistribution/models/PurchaseOrder.js";
import type { RedistributionResult } from "../redistribution/models/RedistributionResult.js";
import { apiUrl } from "./apiBase.ts";
import type {
  SearchFilters,
  SkuForecastsResult,
} from "./purchaseOrderService.ts";
import { getSessionId } from "./sessionId.ts";

function stripDerivedPurchaseOrderFields(
  purchaseOrders: RedistributionRequestPurchaseOrder[]
): RedistributionRequestPurchaseOrder[] {
  return purchaseOrders.map((purchaseOrder) => {
    const sanitizedPurchaseOrder = {
      ...purchaseOrder,
    } as Record<string, unknown>;
    delete sanitizedPurchaseOrder.countUniqueSkus;
    delete sanitizedPurchaseOrder.sumQuantities;
    delete sanitizedPurchaseOrder.cubeTotal;
    delete sanitizedPurchaseOrder.calcArrivalDate;
    return sanitizedPurchaseOrder as RedistributionRequestPurchaseOrder;
  });
}

function buildRequestIdSearchHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
  };
}

const MAX_ERROR_BODY_LENGTH = 500;

/**
 * Extracts a safe, human-readable error message from a failed fetch Response.
 * Infrastructure-level failures (e.g. an Azure Front Door gateway timeout) return a full
 * HTML error page as the response body — that raw markup must never be surfaced verbatim
 * to the user (or thrown as an Error message that gets rendered directly in the UI).
 */
async function extractErrorMessage(response: Response): Promise<string> {
  const rawText = await response.text().catch(() => "");
  const text = rawText.trim();

  if (!text) return response.statusText || `HTTP ${String(response.status)}`;

  if (/^<(!doctype|html)/i.test(text)) {
    return response.status === 504
      ? "The server did not respond in time (gateway timeout). Please try again."
      : `The server returned an unexpected error (HTTP ${String(response.status)}).`;
  }

  return text.length > MAX_ERROR_BODY_LENGTH
    ? `${text.slice(0, MAX_ERROR_BODY_LENGTH)}…`
    : text;
}

export interface RedistributionSubmission {
  requestId: string;
  submittedAt: string;
}

interface RedistributionSettings {
  defaultAlgorithm: AlgorithmType;
  defaultDemandSource: DemandSourceType;
  availableDemandSources: DemandSourceType[];
}

export async function fetchRedistributionSettings(): Promise<RedistributionSettings | null> {
  try {
    const response = await fetch(
      apiUrl("/api/PurchaseOrder/redistribution-settings")
    );
    if (!response.ok) return null;
    return (await response.json()) as RedistributionSettings;
  } catch {
    return null;
  }
}

export async function redistributeSkus(
  purchaseOrders: RedistributionRequestPurchaseOrder[],
  rateOfSaleDays: number,
  poCube: number,
  algorithm: AlgorithmType = "BruteForce",
  allowedReasons?: string[],
  avgDaysOfStockBySku?: SkuForecastsResult,
  demandSource?: DemandSourceType
): Promise<RedistributionSubmission> {
  const requestBody = {
    purchaseOrders: stripDerivedPurchaseOrderFields(purchaseOrders),
    rateOfSaleDays,
    poCube,
    algorithm,
    allowedReasons,
    avgDaysOfStockBySku,
    demandSource,
  };

  return redistributeSkusByRequestBody(requestBody);
}

/**
 * Gzip-compresses a string using the browser's native CompressionStream API, when available.
 *
 * Large redistribute-skus requests (hundreds of POs / tens of thousands of SKUs) can exceed
 * request body size limits as plain JSON. Since JSON compresses very well (typically 80-90%
 * smaller), gzip is used here instead of something like Base64 — which would make the request
 * ~33% *larger*, not smaller. Returns null if CompressionStream isn't supported, so the caller
 * can fall back to sending the body uncompressed.
 */
async function gzipCompress(text: string): Promise<Blob | null> {
  if (typeof CompressionStream === "undefined") return null;

  const stream = new Blob([text])
    .stream()
    .pipeThrough(new CompressionStream("gzip"));
  return await new Response(stream).blob();
}

export async function redistributeSkusByRequestBody(
  requestBody: unknown
): Promise<RedistributionSubmission> {
  const json = JSON.stringify(requestBody);
  const compressedBody = await gzipCompress(json);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Session-Id": getSessionId(),
  };
  if (compressedBody) {
    headers["Content-Encoding"] = "gzip";
  }

  const response = await fetch(apiUrl("/api/PurchaseOrder/redistribute-skus"), {
    method: "POST",
    headers,
    body: compressedBody ?? json,
  });

  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Redistribution API failed (${String(response.status)}): ${message}`
    );
  }

  return (await response.json()) as RedistributionSubmission;
}

export async function cancelRedistribution(requestId: string): Promise<void> {
  const response = await fetch(
    apiUrl(
      `/api/PurchaseOrder/cancel-redistribution/${encodeURIComponent(requestId)}`
    ),
    { method: "POST" }
  );

  if (!response.ok && response.status !== 404) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Cancel request failed (${String(response.status)}): ${message}`
    );
  }
}

export async function fetchRedistributionResult(
  requestId: string
): Promise<RedistributionResult> {
  const response = await fetch(
    apiUrl(
      `/api/PurchaseOrder/redistribution-result/${encodeURIComponent(requestId)}`
    )
  );

  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Failed to fetch result (${String(response.status)}): ${message}`
    );
  }

  return (await response.json()) as RedistributionResult;
}

interface RedistributionStatus {
  requestId: string;
  status: string;
  submittedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

/**
 * Fetches the current status of a redistribution job. Returns `null` when the request ID is
 * unknown or its status has expired (HTTP 404), which the caller can treat as a terminal outcome
 * rather than "still running".
 */
export async function fetchRedistributionStatus(
  requestId: string
): Promise<RedistributionStatus | null> {
  const response = await fetch(
    apiUrl(
      `/api/PurchaseOrder/redistribution-status/${encodeURIComponent(requestId)}`
    )
  );

  if (response.status === 404) return null;

  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Failed to fetch status (${String(response.status)}): ${message}`
    );
  }

  return (await response.json()) as RedistributionStatus;
}

/**
 * Builds a query string from PO search filters. Skips undefined/null values and serializes
 * Date values as ISO strings (mirroring the previous qs.stringify-based behavior) so array-free
 * filter shapes like SearchFilters don't need the qs dependency.
 */
function buildSearchQueryString(filters: SearchFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue;
    params.append(
      key,
      value instanceof Date ? value.toISOString() : String(value)
    );
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

export async function runSearch(
  filters: SearchFilters
): Promise<SearchResponse> {
  const queryString = buildSearchQueryString(filters);
  const response = await fetch(
    apiUrl(`/api/PurchaseOrder/purchaseOrders${queryString}`)
  );
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Search API failed (${String(response.status)}): ${message}`
    );
  }
  const data = await response.json();
  return data as SearchResponse;
}

export async function fetchTraceLog(blobId: string): Promise<string> {
  const response = await fetch(
    apiUrl(`/api/PurchaseOrder/trace-log?blobId=${encodeURIComponent(blobId)}`)
  );
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Trace log fetch failed (${String(response.status)}): ${message}`
    );
  }
  return response.text();
}

export async function runSearchByPONumbers(
  poNumbers: string
): Promise<SearchResponse> {
  const response = await fetch(
    apiUrl("/api/PurchaseOrder/purchaseOrdersByPONumbers"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ poNumbers }),
    }
  );
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Search By PO Numbers API failed (${String(response.status)}): ${message}`
    );
  }
  const data = await response.json();
  return data as SearchResponse;
}

export async function runSearchByRequestId(
  purchaseOrderRequestId: string | number
): Promise<SearchResponse> {
  const response = await fetch(
    apiUrl("/api/PurchaseOrder/purchaseOrdersByRequestId"),
    {
      method: "POST",
      headers: buildRequestIdSearchHeaders(),
      body: JSON.stringify({
        purchaseOrderRequestId: String(purchaseOrderRequestId),
      }),
    }
  );
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Search By Request ID API failed (${String(response.status)}): ${message}`
    );
  }
  const data = await response.json();
  return data as SearchResponse;
}

/**
 * Searches for a Bulk Order (by ParentKey) or Intermediate PO (by OrderNumber),
 * returning every Intermediate PO sibling under the resolved Bulk Order as flat
 * SKU-level rows. When no Intermediate PO rows exist, the result carries
 * view-only Bulk Order allocations and diagnostics explaining why. A 404 is a
 * normal "not found" result, not an error.
 */
export async function searchIntermediateOrdersWithDiagnostics(
  searchTerm: string,
  includeFirstCosts = false
): Promise<IntermediateOrderSearchResult> {
  const response = await fetch(
    apiUrl("/api/PurchaseOrder/searchIntermediateOrders"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        searchTerm,
        ...(includeFirstCosts ? { includeFirstCosts: true } : {}),
      }),
    }
  );
  if (response.status !== 404 && !response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Search Intermediate Orders API failed (${String(response.status)}): ${message}`
    );
  }

  let data: Partial<IntermediateOrderSearchResponseBody> | undefined;
  try {
    const parsed: unknown = await response.json();
    if (parsed && typeof parsed === "object") {
      data = parsed;
    }
  } catch {
    // Older APIs return a plain-text 404 body without diagnostics.
    if (response.ok)
      throw new Error(
        "Search Intermediate Orders API returned an invalid response."
      );
  }

  const rows = data?.response ?? [];
  const bulkAllocations = data?.bulkAllocations ?? [];
  return {
    rows,
    bulkAllocations,
    source:
      data?.source ??
      (rows.length > 0
        ? "IntermediateOrders"
        : bulkAllocations.length > 0
          ? "BulkAllocations"
          : "None"),
    ...(data?.diagnostics ? { diagnostics: data.diagnostics } : {}),
  };
}

interface IntermediateOrderSearchResponseBody {
  response: IntermediateOrderSkuRow[];
  source: IntermediateOrderSearchSource;
  bulkAllocations: BulkOrderAllocationRow[];
  diagnostics?: IntermediateOrderSearchDiagnostics | null;
}

/**
 * Intermediate PO rows only. Returns an empty list on 404 (no match), same
 * convention as runSearchByPONumbers' 404 handling.
 */
export async function searchIntermediateOrders(
  searchTerm: string,
  includeFirstCosts = false
): Promise<IntermediateOrderSkuRow[]> {
  const result = await searchIntermediateOrdersWithDiagnostics(
    searchTerm,
    includeFirstCosts
  );
  return result.rows;
}

export async function generateOptimizedShippingPos(
  request: OptimizedShippingPoRequest,
  signal?: AbortSignal
): Promise<OptimizedShippingPoResponse> {
  return postShippingGeneration(
    "generateSuggestedShippingPos",
    request,
    signal
  );
}

export async function generateDemoShippingPos(
  request: DemoShippingPoRequest,
  signal?: AbortSignal
): Promise<OptimizedShippingPoResponse> {
  return postShippingGeneration(
    "generateDemoSuggestedShippingPos",
    request,
    signal
  );
}

async function postShippingGeneration(
  action: string,
  request: OptimizedShippingPoRequest | DemoShippingPoRequest,
  signal?: AbortSignal
): Promise<OptimizedShippingPoResponse> {
  const response = await fetch(apiUrl(`/api/PurchaseOrder/${action}`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    throw new Error(
      `Suggested PO generation failed (${String(response.status)}): ${await extractErrorMessage(response)}`
    );
  }
  return (await response.json()) as OptimizedShippingPoResponse;
}

export async function runGetRequestIdByPoNumber(
  poNumber: string
): Promise<{ purchaseOrderRequestId: string } | null> {
  const response = await fetch(
    apiUrl(
      `/api/PurchaseOrder/purchaseOrderRequestIdByPoNumber?${new URLSearchParams({ poNumber }).toString()}`
    ),
    { headers: { "Content-Type": "application/json" } }
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(
      `Request ID lookup failed (${String(response.status)}): ${message}`
    );
  }
  return (await response.json()) as { purchaseOrderRequestId: string };
}
